import { useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";

import type { Dayjs } from "@thalamos/common";
import type {
  InterviewContactMethod,
  RouteTimeResult,
  Section12DoctorSearchResult,
  Section12RequestUserStatus,
} from "@thalamos/aspire-common";

import type { DoctorMatchCriteria } from "../utils/doctorMatching.js";
import { splitDoctorsByMatch } from "../utils/doctorMatching.js";
import { DoctorCard } from "./DoctorCard.js";
import { EmptyState } from "./EmptyState.js";
import { Banner, BannerList } from "~/components/design-system/index.js";

interface DoctorListProps {
  doctors: Section12DoctorSearchResult[];
  criteria: DoctorMatchCriteria;
  selectedDoctorIds: Set<string>;
  onToggleSelection: (doctorId: string, selected: boolean) => void;
  existingStatusByUserId?: Map<string, Section12RequestUserStatus>;
  routeTimes?: Map<string, RouteTimeResult>;
  routeTimesLoading?: boolean;
  onContactDoctor?: (
    doctorId: string,
    method: InterviewContactMethod[],
  ) => void;
  contactingDoctorIds?: Set<string>;
  /** UK wall-clock now, for each card's upcoming-availability chips. */
  now?: Dayjs;
}

export function DoctorList({
  doctors,
  criteria,
  selectedDoctorIds,
  onToggleSelection,
  existingStatusByUserId,
  routeTimes,
  routeTimesLoading,
  onContactDoctor,
  contactingDoctorIds,
  now,
}: DoctorListProps) {
  const { matching, nonMatching } = useMemo(
    () => splitDoctorsByMatch(doctors, criteria, routeTimes),
    [doctors, criteria, routeTimes],
  );

  // Sort matching doctors by driving travel time (shortest first, nulls last)
  const sortedMatching = useMemo(() => {
    if (!routeTimes || routeTimes.size === 0) return matching;
    return [...matching].sort((a, b) => {
      const aTime = routeTimes.get(a.id)?.drivingTimeMinutes ?? Infinity;
      const bTime = routeTimes.get(b.id)?.drivingTimeMinutes ?? Infinity;
      return aTime - bTime;
    });
  }, [matching, routeTimes]);

  return (
    <Box>
      {/* Communication warning banner */}
      <Banner
        bannerType={BannerList.WARNING}
        body="Please ensure that doctors have received your chosen method of communication. Phone calls are recommended to facilitate assessment organisation."
        sx={{ mb: 2 }}
      />
      {doctors.length === 0 && (
        <EmptyState
          message="No doctors found"
          suggestion="Try selecting a different local authority or adjusting your filters"
        />
      )}
      {/* Matching Doctors Section */}
      {matching.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            Matching Doctors
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: "normal",
              }}
            >
              ({matching.length})
            </Typography>
          </Typography>
          {sortedMatching.map((doctor) => (
            <DoctorCard
              key={doctor.id}
              doctor={doctor}
              isMatching={true}
              isSelected={selectedDoctorIds.has(doctor.id)}
              onToggleSelection={onToggleSelection}
              existingStatus={existingStatusByUserId?.get(doctor.id)}
              criteria={criteria}
              routeTime={routeTimes?.get(doctor.id)}
              routeTimeLoading={routeTimesLoading}
              onContactDoctor={onContactDoctor}
              isContacting={contactingDoctorIds?.has(doctor.id)}
              now={now}
            />
          ))}
        </Box>
      )}
      {/* Divider between sections if both have doctors */}
      {matching.length > 0 && nonMatching.length > 0 && (
        <Divider sx={{ my: 3 }} />
      )}
      {/* Non-Matching Doctors Section */}
      {nonMatching.length > 0 && (
        <Box>
          <Typography
            variant="h6"
            sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            Other Doctors
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: "normal",
              }}
            >
              ({nonMatching.length})
            </Typography>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            These doctors do not match your current filter criteria but are
            available in the selected local authority.
          </Typography>
          {nonMatching.map(
            ({
              doctor,
              matchedSpecialismCount,
              matchedLanguageCount,
              hasNearAvailability,
              nearAvailabilityStretch,
              matchesGender,
              matchesLanguages,
            }) => (
              <DoctorCard
                key={doctor.id}
                doctor={doctor}
                isMatching={false}
                isSelected={selectedDoctorIds.has(doctor.id)}
                onToggleSelection={onToggleSelection}
                existingStatus={existingStatusByUserId?.get(doctor.id)}
                matchedSpecialismCount={matchedSpecialismCount}
                matchedLanguageCount={matchedLanguageCount}
                hasNearAvailability={hasNearAvailability}
                nearAvailabilityStretch={nearAvailabilityStretch}
                matchesGender={matchesGender}
                matchesLanguages={matchesLanguages}
                criteria={criteria}
                routeTime={routeTimes?.get(doctor.id)}
                routeTimeLoading={routeTimesLoading}
                onContactDoctor={onContactDoctor}
                isContacting={contactingDoctorIds?.has(doctor.id)}
                now={now}
              />
            ),
          )}
        </Box>
      )}
      {/* No doctors match the search, though others were found */}
      {matching.length === 0 && nonMatching.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <EmptyState
            message="No doctors match your search"
            suggestion="Try a different time, or adjust or clear your filters"
          />
          <Divider sx={{ my: 3 }} />
        </Box>
      )}
    </Box>
  );
}
